<!-- Dropzone.vue -->
<template>
  <div
    class="stk-dropzone"
    :class="{
      'is-dragover': isDragOver && !progress,
      'has-progress': progress > 0,
      'progress-done': progress === 100,
      'show-list': showList,
    }"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
    @click="triggerFileInput"
  >
    <Stack
      class="stk-dropzone-body"
      gap="2"
    >
      <Icon
        class="stk-dropzone-icon"
        icon="fas fa-upload"
        resize="6"
      />

      <Title
        class="stk-dropzone-title"
        resize="5"
      >
        Drop your {{ label }} here
      </Title>

      <Button
        variant="primary"
        resize="3_5"
      >
        Browse files
      </Button>

      <Badges
        v-if="files.length && showList"
        class="stk-dropzone-files"
      >
        <Badge
          v-for="(file, index) in files"
          :key="index"
          class="stk-dropzone-file"
          status="primary"
        >
          {{ file.name }}
        </Badge>
      </Badges>
    </Stack>

    <Stack
      class="stk-dropzone-hover"
      gap="2"
    >
      <Title
        class="stk-dropzone-title"
        resize="5"
      >
        Drop your {{ label }} here
      </Title>

      <Icon
        class="stk-dropzone-icon"
        icon="fas fa-arrow-down"
        resize="6"
      />
    </Stack>

    <Stack
      class="stk-dropzone-progress"
      gap="2"
    >
      <Title
        class="stk-dropzone-title"
        resize="5"
      >
        <span v-if="progress === 100">Upload complete</span>
        <span v-else>{{ progress }}%</span>
      </Title>

      <div class="stk-dropzone-bar">
        <div
          class="stk-dropzone-bar-load"
          :style="`--progress: ${progress}`"
        />
      </div>

      <Icon
        v-if="progress === 100"
        class="stk-dropzone-icon"
        icon="fas fa-check"
        resize="6"
      />
      <Icon
        v-else
        class="stk-dropzone-icon"
        icon="fas fa-sync fa-spin"
        resize="6"
      />
    </Stack>

    <input
      ref="fileInput"
      type="file"
      :multiple="multiple"
      :accept="accept"
      hidden
      @change="onFileSelect"
    >

    <div
      class="modal tw-z-[60]"
      :class="{ 'is-active': showNoCreditsModal }"
    >
      <div
        class="modal-background"
        @click.stop="showNoCreditsModal = false"
      />
      <div
        class="modal-card"
        @click.stop
      >
        <header class="modal-card-head">
          <p class="modal-card-title">
            Not enough credits
          </p>
          <button
            class="delete"
            aria-label="close"
            @click.stop="showNoCreditsModal = false"
          />
        </header>
        <section class="modal-card-body">
          <p v-if="canBuyCredits">
            You don't have enough credits to upload files. Visit the Credit Dashboard to buy more credits.
          </p>
          <p v-else>
            You don't have enough credits to upload files. Please contact your firm administrator for assistance.
          </p>
        </section>
        <footer class="modal-card-foot">
          <Button
            v-if="canBuyCredits"
            variant="primary"
            @click.stop="goToCreditDashboard"
          >
            Go to Credit Dashboard
          </Button>
          <Button @click.stop="showNoCreditsModal = false">Close</Button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
import Button from '../../form/button/Button.vue';
import Stack from '../../layout/stack/Stack.vue';
import Icon from '../../graphic/icon/Icon.vue';
import Title from '../../content/text/Title.vue';
import Badges from '../../content/badge/Badges.vue';
import Badge from '../../content/badge/Badge.vue';
import { computed, ref, watch, onUnmounted, getCurrentInstance } from 'vue';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

const props = defineProps({
  multiple: {
    type: Boolean,
    default: true,
  },
  accept: {
    type: String,
  },
  showList: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: Number,
    default: 0,
  },
  onChange: {
    type: Function,
  },
});

const isDragOver = ref(false);
const files = ref([]);
const fileInput = ref(null);
const showNoCreditsModal = ref(false);

let timeoutId = null;

const emit = defineEmits(['reset-progress']);

const onDragOver = () => (isDragOver.value = true);
const onDragLeave = () => (isDragOver.value = false);

const onDrop = event => {
  if (!canUpload.value) {
    showNoCreditsModal.value = true;
    return;
  }
  isDragOver.value = false;
  handleFiles(event.dataTransfer.files);
};

const onFileSelect = event => {
  if (!canUpload.value) {
    showNoCreditsModal.value = true;
    event.target.value = '';
    return;
  }
  handleFiles(event.target.files);
};

const handleFiles = selectedFiles => {
  console.log('handleFiles');
  files.value = [...selectedFiles];

  if (typeof props.onChange === 'function') {
    props.onChange([...selectedFiles]);
  }
};

const triggerFileInput = () => {
  if (!canUpload.value) {
    showNoCreditsModal.value = true;
    return;
  }
  fileInput.value.click();
};

const label = computed(() => props.multiple ? 'files' : 'CSV file');

const advisorStore = useAdvisorStore();
const canUpload = computed(() => {
  const firm = advisorStore.advisor?.firm;
  if (!firm) return true;
  if (!firm.credit_based_firm) return true;
  const creds = firm.extraction_credits
  console.log('FIRM EXTRACTION CREDITS HERE', creds)
  return (firm.extraction_credits ?? 0) >= 1;
});
const canBuyCredits = computed(() => {
  const advisor = advisorStore.advisor;
  if (!advisor) return false;
  if (advisor.is_firm_admin || advisor.can_purchase_credits) return true
  return false;
});

const { proxy } = getCurrentInstance();
const goToCreditDashboard = () => {
  showNoCreditsModal.value = false;
  if (proxy?.$router) proxy.$router.push({ name: 'credit-dashboard' });
};

watch(() => props.progress, newValue => {
  if (newValue === 100) {
    timeoutId = setTimeout(() => {
      emit('reset-progress');
    }, 2000);
  }
});

onUnmounted(() => {
  if (timeoutId) clearTimeout(timeoutId);
});
</script>

<style>
.stk-dropzone {
  --color: var(--stk-primary);
  --color-h: var(--stk-primary-h);
  --color-s: var(--stk-primary-s);
  background-color: hsl(var(--color-h) var(--color-s) 98%);
  border: 1px dashed var(--color);
  display: flex;
  color: var(--color);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 1.5em;
  border-radius: 1em;
  position: relative;
  width: 100%;
  min-height: 9rem;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, border-color, color;

  &:hover {
    background-color: hsl(var(--color-h) var(--color-s) 95%);

    .stk-button {
      background-color: var(--stk-control-hover-background-color);
    }
  }

  &.is-dragover {
    background-color: hsl(var(--color-h) var(--color-s) 95%);
    border-color: hsl(var(--color-h) var(--color-s) 50%);
  }

  &.is-dragover {
    .stk-dropzone-body {
      opacity: 0;
      transform: translateY(-0.5em);
    }

    .stk-dropzone-hover {
      opacity: 1;
      transform: none;
    }
  }

  &.has-progress {
    pointer-events: none;

    .stk-dropzone-body {
      opacity: 0;
      transform: translateY(-0.5em);
    }

    .stk-dropzone-progress {
      opacity: 1;
      transform: none;
    }
  }

  &.progress-done {
    --color: var(--stk-success-50);
    --color-h: var(--stk-success-h);
    --color-s: var(--stk-success-s);
    pointer-events: none;
  }

  &.show-list {
    min-height: 12rem;
  }
}

.stk-dropzone-body {
  align-items: center;
  flex-grow: 1;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;
  pointer-events: none;
}

.stk-dropzone-icon {
  color: inherit;
}

.stk-dropzone-files {
  justify-content: center;
}

.stk-dropzone-progress,
.stk-dropzone-hover {
  height: 4em;
  display: flex;
  align-items: center;
  width: 20em;
  opacity: 0;
  text-align: center;
  justify-content: center;
  position: absolute;
  left: calc(50% - 10em);
  top: calc(50% - 2em);
  transform: translateY(-0.5em);
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity, transform;
  pointer-events: none;
}

.stk-dropzone-bar {
  width: 20em;
  height: 0.5em;
  flex-shrink: 0;
  border-radius: 9999px;
  background-color: #fff;
  display: flex;
  margin-bottom: 0.5em;
  overflow: hidden;
}

.stk-dropzone-bar-load {
  background-color: var(--color);
  width: 100%;
  transform-origin: left center;
  transform: scaleX(calc(var(--progress) / 100));
  transition-duration: var(--stk-transition-duration);
  transition-property: transform;
}
</style>
